import { AlertTriangle, Loader2, RotateCw } from 'lucide-react';
import React from 'react';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    TokenIcon,
    useTokenPairs,
    useTokenPairStore,
} from '@/features/tokenPairs';

interface TokenPairSelectProps {
    disabled?: boolean;
    className?: string;
    /**
     * Borderless trigger showing only the FIRST (base) token of the pair —
     * logo + symbol (e.g. ETH-USDC → "◆ ETH"). The dropdown itself still
     * lists full pairs. Omitted = full-pair trigger.
     */
    iconOnly?: boolean;
}

/**
 * Formats a pair symbol for display
 * e.g., "cro-usd" -> "CRO/USD"
 */
function formatPairDisplay(pair: string): string {
    return pair.replace('-', '/').toUpperCase();
}

/**
 * Token pair icons showing both tokens stacked
 */
function TokenPairIcons({ pair }: { pair: string }) {
    const [base, quote] = pair.split('-');

    return (
        <div className="flex -space-x-1.5">
            <TokenIcon token={base} className="z-10" />
            <TokenIcon token={quote} className="z-0" />
        </div>
    );
}

function TokenPairSelect({
    disabled = false,
    className,
    iconOnly = false,
}: TokenPairSelectProps) {
    // Individual selectors (not whole-store destructuring) so unrelated
    // store writes don't re-render the Select tree.
    const selectedPair = useTokenPairStore((s) => s.selectedPair);
    const setSelectedPair = useTokenPairStore((s) => s.setSelectedPair);
    const networkId = useTokenPairStore((s) => s.networkId);
    const { data, isLoading, isError, refetch } = useTokenPairs(networkId);

    if (isLoading) {
        return (
            <div
                role="status"
                className={`flex h-8 items-center gap-2 rounded-lg border border-border bg-card/50 px-3 py-2 ${className}`}
            >
                <Loader2 className="size-4 animate-spin text-muted-foreground" />
                <span className="font-mono text-xs text-muted-foreground">
                    Loading...
                </span>
            </div>
        );
    }

    if (isError) {
        return (
            <div
                role="alert"
                className={`flex h-8 items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-1 ${className}`}
            >
                <AlertTriangle className="size-4 shrink-0 text-destructive" />
                <span className="font-mono text-xs text-destructive">
                    Pairs unavailable
                </span>
                <button
                    onClick={() => void refetch()}
                    aria-label="Retry loading pairs"
                    className="ml-auto flex size-6 items-center justify-center text-destructive transition-colors hover:text-destructive/80"
                    title="Retry"
                >
                    <RotateCw className="size-3" />
                </button>
            </div>
        );
    }

    const pairs = data?.symbols ?? [];

    return (
        <Select
            value={selectedPair}
            onValueChange={setSelectedPair}
            disabled={disabled}
        >
            <SelectTrigger className={className} size="sm">
                <SelectValue>
                    {iconOnly ? (
                        <div className="flex items-center gap-1.5">
                            <TokenIcon token={selectedPair.split('-')[0]} />
                            <span className="font-mono text-xs font-bold">
                                {selectedPair.split('-')[0]?.toUpperCase()}
                            </span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <TokenPairIcons pair={selectedPair} />
                            <span className="font-mono text-xs font-bold">
                                {formatPairDisplay(selectedPair)}
                            </span>
                        </div>
                    )}
                </SelectValue>
            </SelectTrigger>
            <SelectContent>
                {pairs.map((pair) => (
                    <SelectItem key={pair} value={pair}>
                        <div className="flex items-center gap-2">
                            <TokenPairIcons pair={pair} />
                            <span className="font-mono text-xs font-bold">
                                {formatPairDisplay(pair)}
                            </span>
                        </div>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}

// Memoized: the closed Radix Select keeps its full item collection mounted
// (one subtree per market), so a parent that re-renders per price tick —
// SpeedChartHeader — would otherwise re-render every item
// ~30×/s for zero visual change.
export default React.memo(TokenPairSelect);
